import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';

import { chatApi } from '../api/chat';
import type { ConversationDetailView, ConversationView, SuggestedGroupView } from '../api/chat.types';
import { useChatRealtime } from '../chat/ChatRealtimeContext';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { Button, Tabs } from '../ds';
import { useAsync } from '../hooks/useAsync';
import { ChatThreadPane } from '../screens/chat/ChatThreadPane';
import { ConversationRow } from '../screens/chat/ConversationRow';
import { CreateGroupPanel } from '../screens/chat/CreateGroupPanel';
import { GroupInfoPanel } from '../screens/chat/GroupInfoPanel';
import { Avatar } from '../screens/social/Avatar';
import { useSocial } from '../screens/social/SocialContext';
import { SocialHeader } from '../screens/social/SocialHeader';
import { SocialIcon, SocialIconButton } from '../screens/social/SocialIcon';
import { WebModal } from '../shell/WebModal';

type Tab = 'all' | 'group' | 'dm';
const TABS: { value: Tab; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'group', label: 'Nhóm học' },
  { value: 'dm', label: 'Cá nhân' },
];

const sectionLabel: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-secondary)' };

function SuggestedGroupCard({ groups, onJoined }: { groups: SuggestedGroupView[]; onJoined: (c: ConversationView) => void }) {
  const { toast } = useSocial();
  const [joining, setJoining] = useState<string | null>(null);
  return (
    <div style={{ margin: '12px 8px', padding: 14, borderRadius: 'var(--radius-card)', border: '1px solid var(--border-default)', background: 'var(--surface-tint)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={sectionLabel}>Nhóm gợi ý · cùng tiến độ</span>
      {groups.map((g) => (
        <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={g.name} mark={g.mark} size={38} tone={2} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{g.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{g.focus ? `${g.memberCount} thành viên · ${g.focus}` : `${g.memberCount} thành viên`}</div>
          </div>
          <Button
            size="sm"
            disabled={joining !== null}
            onClick={async () => {
              setJoining(g.id);
              try {
                onJoined(await chatApi.joinGroup(g.id));
              } catch {
                toast('Không tham gia được nhóm, thử lại sau');
              } finally {
                setJoining(null);
              }
            }}
          >
            Tham gia
          </Button>
        </div>
      ))}
    </div>
  );
}

/**
 * Trang Chat bản web: danh sách hội thoại bên trái, cuộc trò chuyện bên phải, thông tin nhóm
 * ở cột thứ ba khi màn rộng. Điện thoại (< 640px) chỉ hiện một khung một lúc như app.
 * Hội thoại đang mở nằm trên URL (`/chat?c=…`) để "Nhắn tin" ở Cộng đồng mở thẳng vào đây.
 */
export function WebChatPage({ narrow, wide, phone }: { narrow: boolean; wide: boolean; phone: boolean }) {
  const { me, toast } = useSocial();
  const { messageTick } = useChatRealtime();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [infoDocked, setInfoDocked] = useState(true);
  const [infoModal, setInfoModal] = useState(false);
  const [detail, setDetail] = useState<ConversationDetailView | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setCreating(false);
      setInfoModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const loaded = useAsync(() => chatApi.listConversations(), [reloadKey, messageTick]);
  const suggested = useAsync(() => chatApi.getSuggestedGroups(), [reloadKey]);

  // Giữ list cũ trong lúc tải lại (mỗi tin mới tải lại một lần), không nháy spinner.
  const [conversations, setConversations] = useState<ConversationView[] | null>(null);
  if (loaded.data && loaded.data !== conversations) setConversations(loaded.data);

  const q = query.trim().toLowerCase();
  const visible = (conversations ?? [])
    .filter((c) => tab === 'all' || (tab === 'group' ? c.kind === 'GROUP' : c.kind === 'DM'))
    .filter((c) => !q || c.name.toLowerCase().includes(q));

  // Máy tính mở sẵn hội thoại đầu tiên như bản thiết kế; điện thoại mở danh sách.
  const selectedId = params.get('c') ?? (phone ? null : (conversations?.[0]?.id ?? null));
  const open = useCallback((id: string) => setParams({ c: id }), [setParams]);
  const onConversation = useCallback((c: ConversationDetailView) => setDetail(c), []);
  const activeDetail = detail && detail.id === selectedId ? detail : null;
  const group = activeDetail?.kind === 'GROUP';

  const afterJoinOrCreate = (c: ConversationView) => {
    setTab('all');
    setReloadKey((k) => k + 1);
    open(c.id);
  };

  const list = (
    <div
      style={{
        width: phone ? '100%' : narrow ? 280 : 340,
        flexShrink: 0,
        borderRight: phone ? 'none' : '1px solid var(--border-default)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 4px 16px' }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Tin nhắn</span>
        <SocialIconButton name="users" label="Tạo nhóm học" onClick={() => setCreating(true)} />
        <SocialIconButton name="edit" label="Tin nhắn mới" onClick={() => toast('Nhắn tin từ thẻ tác giả ở Cộng đồng')} />
      </div>
      <div style={{ padding: '4px 16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 44, borderRadius: 'var(--radius-control)', background: 'var(--surface-tint)', border: '1px solid var(--border-default)', padding: '0 12px', color: 'var(--text-secondary)' }}>
          <SocialIcon name="search" size={17} />
          <input
            className="sk-input"
            aria-label="Tìm người hoặc nhóm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm người hoặc nhóm"
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'none', outline: 'none', fontFamily: 'var(--font-vi)', fontSize: 14, color: 'var(--ink)' }}
          />
        </label>
      </div>
      <div style={{ padding: '0 8px' }}>
        <Tabs items={TABS} active={tab} onChange={(v) => setTab(v as Tab)} />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 8px' }}>
        {loaded.loading && !conversations ? (
          <LoadingState />
        ) : loaded.error && !conversations ? (
          <ErrorState message="Không tải được cuộc trò chuyện" onRetry={() => setReloadKey((k) => k + 1)} />
        ) : (
          <>
            {visible.map((c) => (
              <ConversationRow
                key={c.id}
                // Hội thoại đang mở đã được đánh dấu đọc — không chờ lượt tải lại mới về 0.
                conversation={c.id === selectedId ? { ...c, unread: 0 } : c}
                meId={me?.id}
                active={!phone && c.id === selectedId}
                onOpen={() => open(c.id)}
              />
            ))}
            {visible.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                {q ? 'Không tìm thấy cuộc trò chuyện.' : 'Chưa có cuộc trò chuyện nào.'}
              </div>
            )}
          </>
        )}
        {tab !== 'dm' && !q && !!suggested.data?.length && <SuggestedGroupCard groups={suggested.data} onJoined={afterJoinOrCreate} />}
      </div>
      <div style={{ padding: 12, borderTop: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column' }}>
        <Button variant="secondary" style={{ justifyContent: 'center' }} icon={<SocialIcon name="plus" size={17} />} onClick={() => setCreating(true)}>
          Tạo nhóm học mới
        </Button>
      </div>
    </div>
  );

  const thread = selectedId ? (
    <ChatThreadPane
      key={selectedId}
      conversationId={selectedId}
      onBack={phone ? () => setParams({}) : undefined}
      onConversation={onConversation}
      infoActive={wide ? infoDocked : infoModal}
      onToggleInfo={() => (wide ? setInfoDocked((v) => !v) : setInfoModal(true))}
    />
  ) : (
    <div style={{ margin: 'auto', padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>Chọn một cuộc trò chuyện</div>
  );

  return (
    <div
      style={{
        height: '100%',
        minHeight: phone ? 0 : 560,
        display: 'flex',
        overflow: 'hidden',
        background: 'var(--surface-page)',
        ...(phone ? {} : { border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)' }),
      }}
    >
      {(!phone || !selectedId) && list}
      {(!phone || selectedId) && <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>{thread}</div>}
      {wide && group && infoDocked && activeDetail && <GroupInfoPanel conversation={activeDetail} docked />}

      {infoModal && activeDetail && !wide && (
        <WebModal width={400} onClose={() => setInfoModal(false)}>
          <SocialHeader title="Thông tin nhóm" right={<SocialIconButton name="close" label="Đóng" onClick={() => setInfoModal(false)} />} />
          <GroupInfoPanel conversation={activeDetail} docked={false} />
        </WebModal>
      )}
      {creating && (
        <WebModal width={560} onClose={() => setCreating(false)}>
          <CreateGroupPanel
            onClose={() => setCreating(false)}
            onCreated={(g) => {
              setCreating(false);
              afterJoinOrCreate(g);
            }}
          />
        </WebModal>
      )}
    </div>
  );
}
